import {
  Download,
  ImagePlus,
  MonitorSmartphone,
  Plus,
  RefreshCw,
  RotateCw,
  Scissors,
  Share2,
  Trash2,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import { type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PageGrid } from './PageGrid';
import { PlaceImageEditor, type ChosenImage } from './PlaceImageEditor';
import type { usePdfWorkspace } from './use-pdf-workspace';

export type Workspace = ReturnType<typeof usePdfWorkspace>;

function ActionButton({
  icon,
  label,
  short,
  onClick,
  danger,
  disabled,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly short: string;
  readonly onClick: () => void;
  readonly danger?: boolean;
  readonly disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex min-h-11 min-w-14 flex-col items-center justify-center gap-0.5 rounded-md px-2 text-[11.5px] sm:flex-row sm:gap-1.5 sm:px-2.5 sm:text-sm',
        'hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50',
        danger ? 'text-destructive' : 'text-foreground',
      )}
    >
      {icon}
      <span className="sm:hidden">{short}</span>
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

export interface ToolbarProps {
  readonly phone: boolean;
  readonly selectedCount: number;
  readonly busy: boolean;
  readonly onExtract: () => void;
  readonly onReplace: () => void;
  readonly onRotate: () => void;
  readonly onImage: () => void;
  readonly onDelete: () => void;
}

/** The actions on the selection: a bar above the grid on a computer, a row above the dock on a phone. */
export function SelectionToolbar(p: ToolbarProps) {
  const { t } = useTranslation('tools');
  const { phone } = p;
  return (
    <div
      role="toolbar"
      aria-label={t('pdf.actions.toolbar')}
      className={
        phone
          ? 'flex items-center justify-between gap-1 border-t border-border bg-card px-2 py-1.5'
          : 'flex items-center gap-1 rounded-xl bg-selected px-3 py-2'
      }
    >
      <span className={phone ? 'sr-only' : 'mr-2 text-sm font-semibold'}>
        {t('pdf.grid.selected', { count: p.selectedCount })}
      </span>
      <ActionButton
        icon={<Scissors aria-hidden="true" className="size-5 sm:size-4" />}
        label={t('pdf.actions.extract')}
        short={t('pdf.actions.extract')}
        onClick={p.onExtract}
        disabled={p.busy}
      />
      <ActionButton
        icon={<RefreshCw aria-hidden="true" className="size-5 sm:size-4" />}
        label={t('pdf.actions.replace')}
        short={t('pdf.actions.replaceShort')}
        onClick={p.onReplace}
        disabled={p.busy}
      />
      <ActionButton
        icon={<RotateCw aria-hidden="true" className="size-5 sm:size-4" />}
        label={t('pdf.actions.rotate')}
        short={t('pdf.actions.rotate')}
        onClick={p.onRotate}
      />
      <ActionButton
        icon={<ImagePlus aria-hidden="true" className="size-5 sm:size-4" />}
        label={t('pdf.actions.image')}
        short={t('pdf.actions.imageShort')}
        onClick={p.onImage}
      />
      {!phone && <span className="grow" />}
      <ActionButton
        icon={<Trash2 aria-hidden="true" className="size-5 sm:size-4" />}
        label={t('pdf.actions.delete')}
        short={t('pdf.actions.delete')}
        onClick={p.onDelete}
        danger
      />
    </div>
  );
}

export interface HiddenInputsProps {
  readonly addInput: RefObject<HTMLInputElement | null>;
  readonly replaceInput: RefObject<HTMLInputElement | null>;
  readonly imageInput: RefObject<HTMLInputElement | null>;
  readonly onAdd: (files: File[]) => void;
  readonly onReplace: (file: File) => void;
  readonly onImage: (file: File) => void;
}

export function HiddenInputs(p: HiddenInputsProps) {
  return (
    <>
      <input
        ref={p.addInput}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          const list = [...(e.target.files ?? [])];
          e.target.value = '';
          if (list.length > 0) p.onAdd(list);
        }}
      />
      <input
        ref={p.replaceInput}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) p.onReplace(file);
        }}
      />
      <input
        ref={p.imageInput}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) p.onImage(file);
        }}
      />
    </>
  );
}

export function PageHeader(p: {
  readonly showActions: boolean;
  readonly busy: boolean;
  readonly onAdd: () => void;
  readonly onDownload: () => void;
}) {
  const { t } = useTranslation('tools');
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h2 className="screen-title">{t('pdf.title')}</h2>
        <p className="screen-summary">{t('pdf.summary')}</p>
      </div>
      {p.showActions && (
        <div className="hidden gap-2 sm:flex">
          <Button type="button" variant="outline" onClick={p.onAdd} disabled={p.busy}>
            <Plus aria-hidden="true" />
            {t('pdf.addFiles')}
          </Button>
          <Button type="button" onClick={p.onDownload} disabled={p.busy}>
            <Download aria-hidden="true" />
            {t('pdf.download')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function EmptyDrop(p: { readonly busy: boolean; readonly onChoose: () => void }) {
  const { t } = useTranslation('tools');
  return (
    <div className="flex min-h-56 flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border p-6 text-center">
      <p className="text-sm text-muted-foreground">{t('pdf.empty.text')}</p>
      <Button type="button" onClick={p.onChoose} disabled={p.busy}>
        {p.busy ? t('pdf.working') : t('pdf.empty.choose')}
      </Button>
    </div>
  );
}

export function FilesSummary(p: { readonly ws: Workspace; readonly onAdd: () => void }) {
  const { t } = useTranslation('tools');
  const { ws } = p;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {ws.files.map((f) => (
        <span key={f.id} className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium">
          {t('pdf.files.pages', { name: f.name, count: f.info.pages })}
          {f.info.signed && ` · ${t('pdf.files.signed')}`}
        </span>
      ))}
      <span className="text-sm text-muted-foreground tabular-nums">
        {t('pdf.files.total', { count: ws.pages.length })}
      </span>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="ml-auto h-10 sm:hidden"
        onClick={p.onAdd}
      >
        <Plus aria-hidden="true" />
        {t('pdf.addFilesShort')}
      </Button>
      <span className="basis-full text-sm text-muted-foreground">{t('pdf.files.mergeHint')}</span>
    </div>
  );
}

function noticeText(t: TFunction<'tools'>, n: Workspace['notices'][number]): string {
  if (n.kind === 'failed') return t('pdf.notices.failed');
  const key =
    n.kind === 'password-required' ? 'pdf.notices.passwordRequired' : 'pdf.notices.notPdf';
  return t(key, { name: n.name });
}

/** The messages under the file list: refused files, signed or protected ones, size, and the last hint. */
export function useNoticeTexts(ws: Workspace, message: string | null): string[] {
  const { t } = useTranslation('tools');
  return [
    ...ws.notices.map((n) => noticeText(t, n)),
    ...ws.files.filter((f) => f.info.signed).map((f) => t('pdf.notices.signed', { name: f.name })),
    ...ws.files
      .filter((f) => f.info.wasEncrypted)
      .map((f) => t('pdf.notices.encrypted', { name: f.name })),
    ...(ws.size === 'large' ? [t('pdf.notices.large')] : []),
    ...(message ? [message] : []),
  ];
}

export function GridSection(p: {
  readonly ws: Workspace;
  readonly toolbar: ReactNode;
  readonly onSelect: (key: string, how: 'only' | 'toggle' | 'range') => void;
}) {
  const { t } = useTranslation('tools');
  const { ws } = p;
  return (
    <>
      {p.toolbar}
      <PageGrid
        pages={ws.pages}
        files={ws.files}
        selected={ws.selected}
        onSelect={p.onSelect}
        onMove={ws.move}
      />
      <p className="hidden text-sm text-muted-foreground sm:block">{t('pdf.grid.hintDesktop')}</p>
      <p className="text-sm text-muted-foreground sm:hidden">{t('pdf.grid.hintTouch')}</p>
      <p className="sr-only">{t('pdf.grid.keyboardHint')}</p>
    </>
  );
}

export function LocalNote(p: { readonly working: boolean }) {
  const { t } = useTranslation('tools');
  return (
    <>
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <MonitorSmartphone aria-hidden="true" className="size-4 shrink-0" />
        {t('pdf.localNote')}
      </p>
      {p.working && (
        <p role="status" className="text-sm">
          {t('pdf.working')}
        </p>
      )}
    </>
  );
}

export function WorkspaceSection(p: {
  readonly ws: Workspace;
  readonly notices: readonly string[];
  readonly desktopToolbar: ReactNode;
  readonly onAdd: () => void;
  readonly onSelect: (key: string, how: 'only' | 'toggle' | 'range') => void;
}) {
  const { ws, notices } = p;
  const empty = ws.pages.length === 0;
  return (
    <section className="space-y-3.5 rounded-xl border border-border bg-card p-4 sm:p-5">
      {empty ? (
        <EmptyDrop busy={ws.busy} onChoose={p.onAdd} />
      ) : (
        <FilesSummary ws={ws} onAdd={p.onAdd} />
      )}

      {notices.length > 0 && (
        <ul role="status" className="space-y-1 text-sm">
          {notices.map((n, i) => (
            <li key={`${i}-${n}`} className="rounded-md bg-muted px-3 py-2">
              {n}
            </li>
          ))}
        </ul>
      )}

      {!empty && <GridSection ws={ws} toolbar={p.desktopToolbar} onSelect={p.onSelect} />}
      <LocalNote working={ws.busy && !empty} />
    </section>
  );
}

export function PhoneDock(p: {
  readonly toolbar: ReactNode;
  readonly busy: boolean;
  readonly onShare: () => void;
  readonly onDownload: () => void;
}) {
  const { t } = useTranslation('tools');
  return (
    // On a phone the actions stay in reach above the shell's bottom bar. Sticky inside the
    // content, as the shell's own phone actions are: `.app-shell` is a size container, so a
    // fixed bar would be placed against the whole page, under the tab bar (#413, the Director).
    <div className="sticky bottom-[calc(80px+env(safe-area-inset-bottom,0px))] z-[15] sm:hidden">
      {p.toolbar}
      <div className="flex gap-2 border-t border-border bg-card px-4 py-2.5">
        <Button
          type="button"
          variant="outline"
          className="h-[52px] grow"
          onClick={p.onShare}
          disabled={p.busy}
        >
          <Share2 aria-hidden="true" />
          {t('pdf.share')}
        </Button>
        <Button
          type="button"
          className="h-[52px] grow-[2]"
          onClick={p.onDownload}
          disabled={p.busy}
        >
          <Download aria-hidden="true" />
          {t('pdf.download')}
        </Button>
      </div>
    </div>
  );
}

/** The page, file and size a placed image goes on, or undefined when any of them is gone. */
export function placementTarget(ws: Workspace, key: string) {
  const item = ws.pages.find((q) => q.key === key);
  const file = item ? ws.fileOf(item.source) : undefined;
  const size = file?.info.sizes[item?.page ?? 0];
  return item && file && size ? { item, file, size } : undefined;
}

export function PlacingScreen(p: {
  readonly ws: Workspace;
  readonly placing: { key: string; image: ChosenImage };
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('tools');
  const { ws, placing } = p;
  const target = placementTarget(ws, placing.key);
  if (!target) return null;
  const { item, file, size } = target;
  return (
    <div className="space-y-6">
      <div>
        <h2 className="screen-title">{t('pdf.place.title')}</h2>
        <p className="screen-summary">{`PDF › ${file.name}`}</p>
      </div>
      <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
        <PlaceImageEditor
          thumbs={file.thumbs}
          page={item.page}
          pageSize={{ x: size.x, y: size.y, width: size.width, height: size.height }}
          ownRotation={size.rotation}
          extraRotation={item.rotate}
          pageNumber={ws.pages.indexOf(item) + 1}
          pageCount={ws.pages.length}
          image={placing.image}
          onCancel={p.onClose}
          onPlace={(placed) => {
            ws.placeImage(item.key, placed);
            p.onClose();
          }}
        />
      </section>
    </div>
  );
}
